/**
 * View Service Request page load audit — one line per step in sessionStorage + FrontEndLog/page/.
 * Use when the SP returns data in SSMS but the page/API path fails (pinpoints which HTTP call or map step broke).
 */
import { getCsrpLoggingConfig } from "../services/csrpLoggingConfig";
import { getFrontEndLogRootPath } from "../services/apiClientLog";

const STORAGE_PREFIX = "csrp_vsr_page_log_";
const MAX_ENTRIES = 80;

const utcDayKey = () => new Date().toISOString().slice(0, 10);

const trimTrailingSlash = (s) => String(s || "").replace(/\/+$/, "");

const clip = (s, max = 2000) => {
  const str = String(s ?? "");
  return str.length <= max ? str : `${str.slice(0, max)}…`;
};

const summarizeCounts = (obj) => {
  if (obj == null || typeof obj !== "object") return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v)) out[k] = v.length;
    else if (v && typeof v === "object") out[k] = Object.keys(v).length;
    else out[k] = v;
  }
  return out;
};

export const isViewServiceRequestPageLogEnabled = () => {
  const c = getCsrpLoggingConfig();
  return c.enabled && c.pageLog !== false;
};

const appendSession = (entry) => {
  try {
    const key = STORAGE_PREFIX + utcDayKey();
    const raw = sessionStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    arr.push(entry);
    while (arr.length > MAX_ENTRIES) arr.shift();
    sessionStorage.setItem(key, JSON.stringify(arr));
  } catch {
    // ignore
  }
};

const clientLogHeaders = () => {
  const root = getFrontEndLogRootPath();
  const headers = { "Content-Type": "application/json" };
  if (root) headers["X-Csrp-FrontEnd-Log-Root"] = root;
  return headers;
};

async function postPageLogToServer(payload, getApiBaseUrl) {
  if (!isViewServiceRequestPageLogEnabled()) return;
  if (typeof getApiBaseUrl !== "function") return;
  try {
    const base = trimTrailingSlash(getApiBaseUrl() || "");
    await fetch(`${base}/api/diagnostics/client-page-log`, {
      method: "POST",
      headers: clientLogHeaders(),
      credentials: "include",
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // ignore
  }
}

/**
 * Start a page-load trace. Returns { step(), finish(), fail() }.
 * @param {{ parentSrCode?: string, childSrCode?: string, changedBy?: string, getApiBaseUrl?: () => string }} ctx
 */
export function startViewServiceRequestPageLog(ctx = {}) {
  const traceId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `vsr-${Date.now()}`;
  const base = {
    page: "ViewServiceRequest",
    traceId,
    parentSrCode: clip(ctx.parentSrCode, 80),
    childSrCode: clip(ctx.childSrCode, 80),
    changedBy: clip(ctx.changedBy, 80),
    startedAtUtc: new Date().toISOString(),
  };

  const logStep = (step, status, extra = {}) => {
    const entry = {
      ...base,
      step,
      status,
      atUtc: new Date().toISOString(),
      ...extra,
    };
    if (extra.detail && typeof extra.detail === "object") {
      entry.detail = summarizeCounts(extra.detail);
    }
    if (extra.error) {
      entry.error = clip(extra.error, 1500);
    }
    if (extra.httpStatus != null) entry.httpStatus = extra.httpStatus;
    if (extra.ms != null) entry.ms = extra.ms;

    appendSession(entry);
    if (isViewServiceRequestPageLogEnabled()) {
      // eslint-disable-next-line no-console
      console.info(`[CSRP VSR PAGE ${status}]`, entry);
      postPageLogToServer(entry, ctx.getApiBaseUrl);
    }
    return entry;
  };

  logStep("load-start", "info");

  return {
    traceId,
    step: (name, detail) => logStep(name, "ok", { detail }),
    stepWarn: (name, message, detail) =>
      logStep(name, "warn", { error: message, detail }),
    stepFail: (name, err, detail) => {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        String(err || "unknown error");
      return logStep(name, "fail", {
        error: message,
        httpStatus: err?.response?.status ?? null,
        detail,
      });
    },
    finish: (summary) => logStep("load-complete", "ok", { detail: summary }),
    fail: (err, stepName = "load-failed") => {
      logStep(stepName, "fail", {
        error:
          err?.response?.data?.message ||
          err?.message ||
          String(err || "unknown error"),
        httpStatus: err?.response?.status ?? null,
      });
    },
  };
}

/** Read today's page log from sessionStorage (DevTools: csrpVsrPageLogs()). */
export const getViewServiceRequestPageLogs = (day = utcDayKey()) => {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + day);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const exposeViewServiceRequestPageLogHelpers = () => {
  if (typeof window === "undefined") return;
  try {
    window.csrpVsrPageLogs = (day) => getViewServiceRequestPageLogs(day);
  } catch {
    // ignore
  }
};

exposeViewServiceRequestPageLogHelpers();
